import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { permissionService } from "../../services/permissionService";
import type { Group, Permission } from "../../types/auth";

/**
 * Componente para asignar permisos a grupos
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - dense={true} en listas
 * - Máximo 500 líneas
 */

interface GroupPermissionsProps {
  open: boolean;
  onClose: () => void;
  group: Group | null;
  onSave: (groupId: string, permissionIds: string[]) => Promise<void>;
}

export function GroupPermissions({
  open,
  onClose,
  group,
  onSave,
}: GroupPermissionsProps) {
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open && group) {
      loadData();
    }
  }, [open, group]);

  const loadData = async () => {
    if (!group) return;

    setLoading(true);
    setError(null);

    try {
      // Cargar todos los permisos disponibles usando permissionService
      const permissions = await permissionService.getAllPermissions();
      setAllPermissions(permissions);

      // Cargar permisos actuales del grupo usando permissionService
      const currentPermissions = await permissionService.getGroupPermissions(
        group.id,
      );
      const permissionIds = currentPermissions.map(
        (permission: Permission) => permission.id,
      );
      setGroupPermissions(permissionIds);
      setSelectedPermissions([...permissionIds]);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error cargando permisos disponibles",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSave = async () => {
    if (!group) return;

    setSaving(true);
    setError(null);

    try {
      await onSave(group.id, selectedPermissions);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error guardando cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      // Restaurar selección original al cancelar
      setSelectedPermissions([...groupPermissions]);
      onClose();
    }
  };

  const hasChanges = () => {
    if (selectedPermissions.length !== groupPermissions.length) return true;
    return !selectedPermissions.every((id) => groupPermissions.includes(id));
  };

  const renderPermissionStructure = (estructura: any) => {
    if (!estructura || typeof estructura !== "object") {
      return <Typography variant="caption">Estructura no definida</Typography>;
    }

    const routes = Object.keys(estructura);
    if (routes.length === 0) {
      return <Typography variant="caption">Sin rutas definidas</Typography>;
    }

    return (
      <Stack spacing={1}>
        {routes.slice(0, 3).map((route) => (
          <Chip
            key={route}
            label={route}
            size="small"
            variant="outlined"
            color="primary"
          />
        ))}
        {routes.length > 3 && (
          <Typography variant="caption" color="text.secondary">
            +{routes.length - 3} rutas más
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1}>
          <SecurityIcon color="primary" />
          <Stack>
            <Typography variant="h6">Asignar Permisos</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Grupo: {group?.nombre}
              </Typography>
              {group?.es_sistema && (
                <Chip
                  label="Sistema"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Cargando permisos...
              </Typography>
            </Stack>
          ) : (
            <>
              {/* Resumen de cambios */}
              {hasChanges() && (
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Permisos seleccionados:</strong>{" "}
                    {selectedPermissions.length}
                    {groupPermissions.length > 0 && (
                      <>
                        {" | "}
                        <strong>Permisos actuales:</strong>{" "}
                        {groupPermissions.length}
                      </>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Lista de permisos disponibles */}
              <Typography variant="subtitle1">Permisos Disponibles</Typography>

              {allPermissions.length === 0 ? (
                <Alert severity="warning">
                  No hay permisos disponibles para asignar
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {allPermissions.map((permission) => {
                    const isSelected = selectedPermissions.includes(
                      permission.id,
                    );
                    const wasOriginallySelected = groupPermissions.includes(
                      permission.id,
                    );

                    return (
                      <Accordion key={permission.id} elevation={1}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" spacing={2}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() =>
                                handlePermissionToggle(permission.id)
                              }
                              disabled={saving}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Stack>
                              <Stack direction="row" spacing={1}>
                                <Typography variant="body2">
                                  {permission.nombre}
                                </Typography>
                                {permission.es_sistema && (
                                  <Chip
                                    label="Sistema"
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                )}
                                {wasOriginallySelected && !isSelected && (
                                  <Chip
                                    label="Se eliminará"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                                {!wasOriginallySelected && isSelected && (
                                  <Chip
                                    label="Se agregará"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {permission.descripcion}
                              </Typography>
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={2}>
                            <Typography variant="subtitle2">
                              Estructura de Permisos:
                            </Typography>
                            {renderPermissionStructure(
                              permission.estructura_permisos,
                            )}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}

              {/* Información adicional */}
              <Alert severity="info">
                <Typography variant="body2">
                  Los permisos asignados a este grupo se aplicarán
                  automáticamente a todos los usuarios que pertenezcan al grupo.
                  Los permisos del sistema pueden tener funcionalidades
                  especiales.
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={saving}
          size="small"
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || !hasChanges()}
          variant="contained"
          size="small"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
